import qs from 'query-string';

/**
 * Sets the query-string value for the given key.
 * @param key The key to set.
 * @param value Query value to set.
 * @param queryString (optional) The query-string to modify.
 * @returns Modified query-string.
 */
const setQuery = (
  key: string,
  value: string | number | string[],
  queryString?: string
): string => {
  const { search } = window.location;
  const values = qs.parse(queryString || search, {
    arrayFormat: 'bracket',
    sort: false,
    parseNumbers: true,
  });

  const newQsValue = qs.stringify(
    { ...values, [key]: value },
    {
      arrayFormat: 'bracket',
      skipEmptyString: true,
      skipNull: true,
      sort: false,
    }
  );

  return newQsValue;
};

/**
 * Sets the query-string values for multiple keys.
 * @param queryValues Object containing the key-value pairs to set.
 * @param queryString (optional) The query-string to modify.
 * @returns Modified query-string.
 */
const setAllQuery = (
  queryValues: Record<string, string | number | string[]>,
  queryString?: string
): string => {
  const { search } = window.location;
  const values = qs.parse(queryString || search, {
    arrayFormat: 'bracket',
    sort: false,
    parseNumbers: true,
  });

  const newQueryString = qs.stringify(
    { ...values, ...queryValues },
    {
      arrayFormat: 'bracket',
      skipEmptyString: true,
      skipNull: true,
      sort: false,
    }
  );

  return newQueryString;
};

/**
 * Gets the query-string value for the given key.
 * @param key The key to get.
 * @param queryString (optional) The query-string to get from.
 * @returns Query-string value.
 */
const getQuery = (
  key: string,
  queryString?: string
): string | number | null => {
  const { search } = window.location;

  const values = qs.parse(queryString || search, {
    sort: false,
    parseNumbers: true,
  });

  return values[key] as string | number;
};

/**
 * Gets the query-string values for given key, if there are multiples values for the key
 * @param key The key to get.
 * @param queryString (optional) The query-string to get from.
 * @returns Array of Query-string values.
 */
const getAllQuery = (key: string, queryString?: string): string[] | null => {
  const { search } = window.location;

  const values = qs.parse(queryString || search, {
    arrayFormat: 'bracket',
    sort: false,
  });

  return values[key] as string[];
};

/**
 * Gets the query-string values.
 * @param queryString (optional) The query-string to get from.
 * @returns Object containing the query-string values.
 */
const parseQuery = (queryString?: string): qs.ParsedQuery<string | number> => {
  const { search } = window.location;

  const parsedQuery = qs.parse(queryString || search, {
    arrayFormat: 'bracket',
    sort: false,
    parseNumbers: true,
  });

  return parsedQuery;
};

/**
 * Stringify the given key-value pairs.
 * @param queryValues Object containing the key-value pairs to set.
 * @returns query-string.
 */
const stringifyQuery = (
  queryValues: Record<string, string | number | string[]>
): string => {
  const stringifiedQuery = qs.stringify(queryValues, {
    arrayFormat: 'bracket',
    skipEmptyString: true,
    skipNull: true,
    sort: false,
  });

  return stringifiedQuery;
};
const query = {
  set: setQuery,
  get: getQuery,
  getAll: getAllQuery,
  setAll: setAllQuery,
  parse: parseQuery,
  stringify: stringifyQuery,
};

export default query;
